// web/src/main.tsx
import React, { StrictMode, type ReactNode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { getApps } from "firebase/app";
import { BadgeProvider } from "@/lib/useBadgeTally";
import { AuthProvider } from "@/lib/auth";
import { ToastProvider, Toaster } from "@/components/ui/use-toast";
import App from "./App";
import "./index.css";
import { TourProvider } from "@/tour/TourProvider";
import GlobalErrorBoundary from "./components/GlobalErrorBoundary";
import UnderConstruction from "./components/UnderConstruction"; // (route below)
import ErrorBoundary from "@/components/ErrorBoundary";

// Log Firebase project id (if available)
try {
  const app = getApps()[0];
  console.info("[Firebase] projectId:", app?.options?.projectId || "(unknown)");
} catch {
  console.info("[Firebase] projectId: (unknown)");
}

function RootErrorBoundary({ children }: { children: ReactNode }) {
  const [err, setErr] = useState<Error | null>(null);

  useEffect(() => {
    const onErr = (e: ErrorEvent) => {
      setErr(e.error || new Error(String(e.message)));
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      setErr(
        (e.reason instanceof Error
          ? e.reason
          : new Error(
              typeof e.reason === "string" ? e.reason : JSON.stringify(e.reason),
            )) as Error,
      );
    };

    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  if (err) {
    return (
      <div
        style={{
          padding: 16,
          color: "#fff",
          background: "#0b0620",
          fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>
          Something went wrong
        </h1>
        <p style={{ opacity: 0.8, marginTop: 8 }}>
          Try refreshing the page. If this keeps happening, please report it
          from the Support page.
        </p>
        <pre
          style={{
            marginTop: 12,
            whiteSpace: "pre-wrap",
            background: "#111827",
            padding: 12,
            borderRadius: 8,
            overflow: "auto",
          }}
        >
          {String(err.stack || err.message)}
        </pre>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 12,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #4c1d95",
            background: "#6d28d9",
            color: "#fff",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

// Clear IndexedDB when project/database id changes
(() => {
  const currentDbId = import.meta.env.VITE_FIREBASE_DATABASE_ID || "(default)";
  const stored = localStorage.getItem("sg_dbid");
  if (stored !== currentDbId) {
    const canList =
      typeof indexedDB !== "undefined" && typeof (indexedDB as any).databases === "function";
    if (canList) {
      (indexedDB as any)
        .databases()
        .then((dbs: Array<{ name?: string }>) => {
          dbs?.forEach((d) => d?.name && indexedDB.deleteDatabase(d.name));
        })
        .finally(() => {
          localStorage.setItem("sg_dbid", currentDbId);
          location.reload();
        });
    } else {
      localStorage.setItem("sg_dbid", currentDbId);
      location.reload();
    }
  }
})();

// Optional: mark JS-enabled to help CSS avoid FOUC
document.documentElement.classList.add("js");

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error('Missing root element with id="root"');

const appTree = (
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <RootErrorBoundary>
          <BadgeProvider>
            <ToastProvider>
              <TourProvider>
                <App />
              </TourProvider>
              <Toaster position="bottom-right" />
            </ToastProvider>
          </BadgeProvider>
        </RootErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>
);

// React 18 mount
const root = createRoot(rootEl);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <GlobalErrorBoundary>
        <App />
      </GlobalErrorBoundary>
    </BrowserRouter>
  </React.StrictMode>,
);

(function installChunkFailRedirect() {
  let armed = false;
  window.addEventListener("error", (ev: any) => {
    const msg = String(ev?.message || "");
    if (/Loading chunk [\d]+ failed/i.test(msg) || /ChunkLoadError/i.test(msg)) {
      if (armed) return;
      armed = true;
      sessionStorage.setItem("__redirected_construction", "1");
      window.location.assign("/Underconstruction");
    }
  });
  window.addEventListener("unhandledrejection", (ev: any) => {
    const msg = String(ev?.reason?.message || ev?.reason || "");
    if (/Loading chunk [\d]+ failed/i.test(msg) || /ChunkLoadError/i.test(msg)) {
      if (armed) return;
      armed = true;
      sessionStorage.setItem("__redirected_construction", "1");
      window.location.assign("/Underconstruction");
    }
  });
})();

